import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { scrollToSection } from "../../lib/scroll";

const lines = ["Governance rebuilt", "through", "First Nations authority."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex items-end overflow-hidden grain -mt-16 pt-16">
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          y: bgY,
          scale: 1.15,
          backgroundImage: "url(https://images.unsplash.com/photo-1601173073177-c0faa03462e6?q=80&w=2000&auto=format&fit=crop)",
        }}
      />
      <div className="absolute inset-0 bg-ink/85" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-8 pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-8"
          data-testid="hero-kicker"
        >
          Bigibila Research · First Nations-led governance methodology
        </motion.p>

        <h1 data-testid="hero-heading" className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
          {lines.map((line, i) => (
            <span key={line} className="hero-line-mask">
              <motion.span
                className={`block ${i === 2 ? "text-ember italic" : "text-bone"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.45 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 grid md:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="md:col-span-6 max-w-xl"
          >
            <p className="text-base md:text-lg text-stone2 leading-relaxed" data-testid="hero-subtext">
              Bigibila delivers First Nations-led governance analysis,
              institutional accountability and Indigenous Data Sovereignty
              services. We identify systemic gaps, cultural risks and practical
              pathways for accountable reconstruction.
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ochre mt-5" data-testid="hero-supporting">
              Delivered through proprietary, culturally governed Bigibila methodologies.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
          >
            <button
              type="button"
              onClick={() => scrollToSection("briefing")}
              data-testid="hero-cta-briefing"
              className="font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300"
            >
              Request a Confidential Briefing
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("services")}
              data-testid="hero-cta-services"
              className="font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 border border-line text-bone hover:border-ember hover:text-ember transition-colors duration-300"
            >
              Explore Bigibila Services
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
